import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ListGroup,
  Badge,
  Image,
  ProgressBar,
  Modal,
  Spinner,
} from "react-bootstrap";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
} from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import QRFunnelChart from "./components/QRFunnelChart";
import QRFunnelSummary from "./components/QRFunnelSummary";
import {
  fetchMonthlyReportById,
  generateMonthlyReportAISummary,
  resetReportAISummary,
} from "../../redux/slices/MonthlyReportSlice";
import { Atom } from "react-loading-indicators";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faQuestionCircle,
  faShare,
  faUserSlash,
} from "@fortawesome/free-solid-svg-icons";
import { message } from "antd";
import { FRONTEND_URL } from "../../config";

// Register Chart.js components
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip
);

/**
 * Tipkit Monthly Report Dashboard
 * - React Bootstrap layout
 * - Chart.js via react-chartjs-2
 *
 * Props: { report }
 */

function buildStaffListFromReport(report) {
  const perf = report?.topPerformers || [];
  const items = perf.map((p) => ({
    name: `${p.firstName || ""} ${p.lastName || ""}`.trim() || "(unknown)",
    checkIns: report?.checkInVsTips?.checkIns
      ? Math.max(
          1,
          Math.floor(report.checkInVsTips.checkIns / (perf.length || 1))
        )
      : 0,
    tips: p.totalTips || 0,
    profileImage: p.profileImage || "",
  }));
  return items;
}

const demoReport = {
  period: {
    start: "2025-11-01T00:00:00.000Z",
  },
  totalTipsCount: 2847,
  totalTipsAmount: 1849200,
  uniqueTippers: 1564,
  highestTipAmount: 25000,
  averageTipAmount: 650,
  unverifiedTipsCount: 23,
  topDays: [
    { date: "2025-11-01", totalTips: 82000, tipsCount: 9 },
    { date: "2025-11-05", totalTips: 164000, tipsCount: 17 },
    { date: "2025-11-08", totalTips: 210000, tipsCount: 22 },
    { date: "2025-11-11", totalTips: 293000, tipsCount: 31 },
    { date: "2025-11-15", totalTips: 275000, tipsCount: 29 },
    { date: "2025-11-18", totalTips: 268000, tipsCount: 27 },
    { date: "2025-11-22", totalTips: 241000, tipsCount: 26 },
    { date: "2025-11-25", totalTips: 265000, tipsCount: 28 },
    { date: "2025-11-28", totalTips: 198000, tipsCount: 21 },
  ],
  tipsBreakdown: {
    individual: { count: 1900, amount: 1201980 },
    pool: { count: 947, amount: 647220 },
    individualSharePct: 0.65,
    poolSharePct: 0.35,
  },
  qrOverview: {
    totalScans: 1247,
    totalTipIntents: 780, // sessions where a tip was selected/entered
    totalSuccessfulTips: 620, // successful payments

    scanToTipIntentConversion: 0.625, // 62.5%
    tipIntentToSuccessConversion: 0.795, // 79.5%
    scanToSuccessConversion: 0.497, // 49.7%

    dailyFunnel: [
      { date: "2025-11-01", scans: 35, tipIntents: 20, successfulTips: 15 },
      { date: "2025-11-02", scans: 42, tipIntents: 25, successfulTips: 18 },
      { date: "2025-11-03", scans: 50, tipIntents: 32, successfulTips: 24 },
      { date: "2025-11-04", scans: 46, tipIntents: 30, successfulTips: 22 },
      { date: "2025-11-05", scans: 58, tipIntents: 36, successfulTips: 29 },
      { date: "2025-11-06", scans: 60, tipIntents: 39, successfulTips: 31 },
      { date: "2025-11-07", scans: 55, tipIntents: 35, successfulTips: 27 },

      // …you can add the whole month if you like
    ],
  },
  topPerformers: [
    {
      firstName: "Sarah",
      lastName: "Johnson",
      totalTips: 124000,
      tipsCount: 28,
      highestTip: 35000,
      profileImage: "",
    },
    {
      firstName: "Michael",
      lastName: "Chen",
      totalTips: 118000,
      tipsCount: 26,
      highestTip: 30000,
      profileImage: "",
    },
    {
      firstName: "Emma",
      lastName: "Davis",
      totalTips: 98000,
      tipsCount: 25,
      highestTip: 22000,
      profileImage: "",
    },
  ],
  checkInVsTips: {
    enabled: true,
    checkIns: 123,
    tippersFromCheckIns: 88,
    tipsFromCheckIns: 92000,
  },
  inactiveStaff: {
    total: 5,
    attendants: 2,
    valets: 3,
    bellDesk: 0,
  },
};
const MonthlyReport = () => {
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  const location = useLocation();
  const dispatch = useDispatch();

  const queryParams = new URLSearchParams(location.search);
  const reportId = queryParams.get("reportId");

  const {
    reportLoading,
    reportError,
    report,

    loading: summaryLoading,
    error: summaryError,
    summary: aiSummary,
  } = useSelector((state) => state.monthlyReports);

  const handleShare = async () => {
    const href = `${FRONTEND_URL}/report?reportId=${reportId}`;
    try {
      await navigator.clipboard.writeText(href);
      message.success("Link copied to clipboard!");
    } catch (err) {
      console.error("Copy failed:", err);
      message.error("Failed to copy link.");
    }
  };

  useEffect(() => {
    if (reportId) {
      dispatch(fetchMonthlyReportById(reportId));
    }
  }, [dispatch, reportId]);

  // const fmtMoney = (centsOrUsd) => {
  //   // Report schema says cents; but allow USD too (small numbers) by heuristic
  //   const n = Number(centsOrUsd || 0);
  //   const dollars = n > 1000 ? n / 100 : n; // treat >$10 as cents input
  //   return dollars.toLocaleString(undefined, {
  //     style: "currency",
  //     currency: "USD",
  //     maximumFractionDigits: 2,
  //   });
  // };
  const fmtMoney = (centsOrUsd) => {
    const n = Number(centsOrUsd || 0);
    const dollars = n > 1000 ? n / 100 : n;
    // Format manually with $ symbol
    return `$${dollars.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const fmtPct = (value) => `${Math.round((Number(value) || 0) * 100)}%`;

  const handleSummarizeWithAI = () => {
    setShowSummaryModal(true);
    dispatch(generateMonthlyReportAISummary(report))
      .unwrap()
      .then(() => {
        // setShowSummaryModal(true);
      })
      .catch(() => {
        // Even on error we show modal so they see the error
        setShowSummaryModal(true);
      });
  };

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false);
    dispatch(resetReportAISummary());
  };

  const monthLabel = report?.period
    ? new Date(report.period.start).toLocaleString(undefined, {
        month: "long",
        year: "numeric",
      })
    : "This Month";

  // --- Charts ---
  // Top earning days -> Bar
  const topDays = report?.topDays || [];
  const topDaysData = {
    labels: topDays.map((d) => d.date.replace(/\b\d{4}-/, "")),
    datasets: [
      {
        label: "Total Tips",
        data: topDays.map((d) => d.totalTips),
        borderWidth: 1,
        backgroundColor: "rgba(54,162,235,0.5)",
        borderColor: "rgba(54,162,235,1)",
      },
    ],
  };
  const topDaysOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => `$${(v / 100).toFixed(0)}` },
      },
    },
  };

  // Tip type distribution -> Pie
  const indAmt = report?.tipsBreakdown?.individual?.amount || 0;
  const poolAmt = report?.tipsBreakdown?.pool?.amount || 0;
  const tipPieData = {
    labels: [
      `Individual Tips: ${fmtPct(
        report?.tipsBreakdown?.individualSharePct || 0
      )}`,
      `Pool Tips: ${fmtPct(report?.tipsBreakdown?.poolSharePct || 0)}`,
    ],
    datasets: [
      {
        data: [indAmt, poolAmt],
        backgroundColor: ["#4e79a7", "#a0cbe8"],
        borderWidth: 0,
      },
    ],
  };

  // QR analytics -> Line (weekly points if provided, else total as single)
  const scansByLocation = report?.qrOverview?.scansByLocation || [];
  const qrLineData = {
    labels: scansByLocation.map((x) => x.location),
    datasets: [
      {
        label: "QR Scans",
        data: scansByLocation.map((x) => x.scans),
        borderColor: "#6c757d",
        backgroundColor: "rgba(108,117,125,0.2)",
        tension: 0.3,
        fill: true,
      },
    ],
  };
  const qrLineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true } },
  };

  // Top performers -> Horizontal Bar (by totalTips)
  const performers = report?.topPerformers || [];
  const perfData = {
    labels: performers.map(
      (p) => `${p.firstName || ""} ${p.lastName || ""}`.trim() || "(unknown)"
    ),
    datasets: [
      {
        label: "Total Tips",
        data: performers.map((p) => p.tipsCount),
        backgroundColor: "rgba(75,192,192,0.6)",
        borderColor: "rgba(75,192,192,1)",
        borderWidth: 1,
      },
    ],
  };
  const perfOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.raw} tips`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        title: {
          display: true,
          text: "Number of Tips",
        },
      },
    },
  };

  if (!report) return null;

  return (
    <Container fluid className="px-5 py-4" style={{ background: "#f8f9fb" }}>
      {/* ---------------------------------------------------------------- AI Summary ---------------------------------------------------------------- */}
      <Modal
        show={showSummaryModal}
        onHide={handleCloseSummaryModal}
        backdrop="static"
        keyboard={false}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <AutoAwesomeIcon fontSize="small" style={{ marginRight: 8 }} />
            Insights &amp; Recommendations
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div
            style={{
              minHeight: 280,
              maxHeight: "60vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: summaryLoading ? "center" : "flex-start",
            }}
          >
            {summaryLoading && (
              <div className="d-flex align-items-center justify-content-center my-3">
                <div
                  style={{
                    transform: "scale(0.5)",
                  }}
                >
                  <Atom color="#004172" />
                </div>
                <span>Generating your summary…</span>
              </div>
            )}

            {!summaryLoading && summaryError && (
              <div className="text-danger">{summaryError}</div>
            )}

            {!summaryLoading && !summaryError && (
              <>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {aiSummary || "No summary available."}
                </div>

                <div
                  className="d-flex align-items-center justify-content-center small text-muted mt-3"
                  style={{ fontSize: "11px" }}
                >
                  <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                  AI can make mistakes. Please verify important information.
                </div>
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* ---------------------------------------------------------------- Header ---------------------------------------------------------------- */}
      <Row className="align-items-center mb-4">
        <Col>
          <h3 className="mb-0">Reports &amp; Statistics</h3>
          <div className="text-muted">{monthLabel} Overview</div>
        </Col>
        <Col className="text-end">
          <Button variant="outline-dark" className="me-2" onClick={handleShare}>
            <FontAwesomeIcon icon={faShare} className="me-2" />
            Share
          </Button>
          <Button
            variant="outline-primary"
            className="ai-magic-button"
            onClick={handleSummarizeWithAI}
            disabled={summaryLoading}
          >
            {summaryLoading ? (
              <>Summarizing...</>
            ) : (
              <>
                <AutoAwesomeIcon fontSize="small" style={{ marginRight: 6 }} />{" "}
                Summarize with AI
              </>
            )}
          </Button>
        </Col>
      </Row>

      {/* ---------------------------------------------------------------- Key Metrics ---------------------------------------------------------------- */}
      <Row
        // xs={1}
        // sm={2}
        // md={3}
        // lg={6}
        className="g-3 mb-4 justify-content-center"
      >
        <Col>
          <Card className="h-100 shadow-sm rounded-3">
            <Card.Body>
              <Card.Title className="mb-3">Total Tips Received</Card.Title>
              <h4 className="mb-0">
                {report.totalTipsCount?.toLocaleString() || 0}
              </h4>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="h-100 shadow-sm rounded-3">
            <Card.Body>
              <Card.Title className="mb-3">Total Tips Earnings</Card.Title>
              <h4 className="mb-0">{fmtMoney(report.totalTipsAmount)}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="h-100 shadow-sm rounded-3">
            <Card.Body>
              <Card.Title className="mb-3">Guests Who Tipped</Card.Title>
              <h4 className="mb-0">
                {report.uniqueTippers?.toLocaleString() || 0}
              </h4>
              {/* <small className="text-success">+ last month</small> */}
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="h-100 shadow-sm rounded-3">
            <Card.Body>
              <Card.Title className="mb-3">Highest Tip Amount</Card.Title>
              <h4 className="mb-0">{fmtMoney(report.highestTipAmount)}</h4>
              {/* <small className="text-success">+ last month</small> */}
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="h-100 shadow-sm rounded-3">
            <Card.Body>
              <Card.Title className="mb-3">Average Tip Amount</Card.Title>
              <h4 className="mb-0">{fmtMoney(report.averageTipAmount)}</h4>
              {/* <small className="text-success">+ last month</small> */}
            </Card.Body>
          </Card>
        </Col>
        {/* <Col>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <Card.Title className="mb-1">Unverified Tips</Card.Title>
              <h4 className="mb-0">{report.unverifiedTipsCount || 0}</h4>
              <small className="text-muted">Pending verification</small>
            </Card.Body>
          </Card>
        </Col> */}
      </Row>

      {/* ---------------------------------------------------------------- Charts Row ---------------------------------------------------------------- */}
      <Row className="g-3 mb-4">
        <Col lg={8}>
          <Card
            className="h-100 shadow-sm rounded-3"
            style={{ minHeight: 220 }}
          >
            <Card.Body>
              <Card.Title>Top Earning Days Of This Month</Card.Title>
              <div style={{ height: 220 }}>
                <Bar data={topDaysData} options={topDaysOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Row className="g-3">
            <Col>
              <Card
                className="h-100 shadow-sm rounded-3"
                style={{ minHeight: 220 }}
              >
                <Card.Body>
                  <Card.Title>Tip Distribution</Card.Title>
                  <div
                    style={{
                      height: 220,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Pie data={tipPieData} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
      {/* ----------------------------------------------------------- QR Code Conversions ---------------------------------------------------------------- */}
      <Row className="g-3 mb-2">
        <Col lg={8}>
          <QRFunnelChart qrOverview={report.qrOverview} />
        </Col>
        <Col lg={4}>
          <QRFunnelSummary qrOverview={report.qrOverview} />
        </Col>
      </Row>
      {/* --------------------------------------------------------- Staff Performers & Tips Ratio ------------------------------------------------------ */}
      <Row className="g-3">
        <Col lg={8}>
          <Card
            className="h-100 shadow-sm rounded-3"
            style={{ minHeight: 300 }}
          >
            <Card.Body>
              <Card.Title className="text-center mt-2">
                Top 5 Performers
              </Card.Title>
              <div className="d-flex align-items-center h-100">
                <div style={{ height: 380 }} className="w-100">
                  <Bar data={perfData} options={perfOptions} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Row className="g-3">
            <Col xs={12}>
              <Card
                className="h-100 shadow-sm rounded-3"
                style={{ minHeight: 150 }}
              >
                <Card.Body>
                  <Card.Title className="mb-3">Tip Mix This Month</Card.Title>

                  <div className="d-flex justify-content-between align-items-baseline mb-1">
                    <span className="text-muted small">Individual tips</span>
                    <span className="fw-semibold">
                      {report?.tipsBreakdown?.individual?.count || 0}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-baseline mb-1">
                    <span className="text-muted small">Pool tips</span>
                    <span className="fw-semibold">
                      {report?.tipsBreakdown?.pool?.count || 0}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-baseline mb-1">
                    <span className="text-muted small">Total tips</span>
                    <span className="fw-semibold">{report.totalTipsCount}</span>
                  </div>
                </Card.Body>
              </Card>
              {/* <Card className="h-100 shadow-sm" style={{ minHeight: 150 }}>
                <Card.Body>
                  <Card.Title>Scan-to-Tip Conversion</Card.Title>
                  <h4 className="mb-1">
                    {(report.qrOverview?.scanToTipConversion * 100).toFixed(1)}%
                  </h4>
                  <div className="text-muted small">
                    of QR scans resulted in tips
                  </div>
                </Card.Body>
              </Card> */}
            </Col>
            <Col xs={12}>
              <Card
                className="h-100 shadow-sm border-danger rounded-3"
                // style={{ minHeight: 150 }}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <Card.Title>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="text-danger"
                          style={{ fontSize: "17px" }}
                        />
                        <span className="ms-1">Unverified Pool Tips</span>
                      </div>
                    </Card.Title>
                    <Badge bg="danger" text="light">
                      Pending
                    </Badge>
                  </div>
                  <h4 className="mb-2">{report.unverifiedTipsCount || 0}</h4>
                  <div className="text-muted small">
                    {report.unverifiedTipsCount} pool tips are still unverified
                    from this month
                  </div>
                </Card.Body>
              </Card>
            </Col>
            {/* Inactive staff summary */}
            {report?.inactiveStaff?.total > 0 && (
              <Col xs={12}>
                <Card className="shadow-sm rounded-3 border-warning">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <Card.Title className="mb-0">
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon
                            icon={faUserSlash}
                            className="text-warning"
                            style={{ fontSize: "14px" }}
                          />
                          <span className="ms-2">Inactive Staff</span>
                        </div>
                      </Card.Title>

                      <Badge bg="warning" text="light">
                        Attention
                      </Badge>
                    </div>

                    <h4 className=" mb-2">{report.inactiveStaff.total}</h4>

                    <div className="text-muted small mb-2">
                      The following staff categories have inactive members:
                    </div>
                    {report.inactiveStaff.attendants > 0 && (
                      <div className="d-flex justify-content-between align-items-baseline mb-1">
                        <span className="text-muted small">Attendants</span>
                        <span className="fw-semibold">
                          {report.inactiveStaff.attendants || 0}
                        </span>
                      </div>
                    )}

                    {report.inactiveStaff.valets > 0 && (
                      <div className="d-flex justify-content-between align-items-baseline mb-1">
                        <span className="text-muted small">Valets</span>
                        <span className="fw-semibold">
                          {report.inactiveStaff.valets || 0}
                        </span>
                      </div>
                    )}

                    {report.inactiveStaff.bellDesk > 0 && (
                      <div className="d-flex justify-content-between align-items-baseline mb-1">
                        <span className="text-muted small">BellDesk</span>
                        <span className="fw-semibold">
                          {report.inactiveStaff.bellDesk || 0}
                        </span>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default MonthlyReport;

// --- Demo Data (so the component can render immediately) ---
